<template>
    <div class="flex flex-wrap items-center justify-center gap-3">
        <label v-if="setPerPage" class="text-primary-50/50 flex items-center gap-2 text-sm">
            <span><t path="generic.per_page" /></span>
            <select class="input h-10 w-20 text-sm" :value="paginationData.perPage" @change="onPerPageChange">
                <option v-for="option in normalizedPerPageOptions" :key="option" :value="option">
                    {{ option }}
                </option>
            </select>
        </label>

        <ul v-if="paginationData.totalPages > 1">
            <li :class="firstPage ? ['disabled'] : []">
                <button type="button" class="p-4 md:py-3" @click.prevent="setPage(currentPage - 1)">‹</button>
            </li>
            <li v-for="page of pageElements" :key="page" :class="(currentPage === page && ['active']) || (page === '...' && ['disabled']) || []">
                <button type="button" class="p-4 md:py-3" @click.prevent="setPage(page)">
                    {{ page }}
                </button>
            </li>
            <li :class="lastPage ? ['disabled'] : []">
                <button type="button" class="p-4 md:py-3" @click.prevent="setPage(currentPage + 1)">›</button>
            </li>
        </ul>
    </div>
</template>

<style scoped>
ul {
    display: flex;
    padding-left: 0;
    list-style: none;
    border-radius: 0.25rem;
}

a,
button {
    display: inline-block;
    line-height: 1.25;

    background: var(--color-primary-700);
    color: --alpha(var(--color-primary-50) / 50%) !important;
    border: none !important;
    box-shadow: none !important;
}

a:hover,
button:hover {
    background: var(--color-primary-600);
}

button:focus {
    outline: none;
}

li.active > button {
    pointer-events: none;
    border: none !important;
    background: var(--color-primary-600) !important;
}

li.disabled > button {
    pointer-events: none;
    border: none !important;
    background: --alpha(var(--color-primary-50) / 2.5%) !important;
}

li:first-child > button {
    border-radius: 0.25rem 0 0 0.25rem;
}

li:last-child > button {
    border-radius: 0 0.25rem 0.25rem 0;
}
</style>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';

export default defineComponent({
    props: {
        paginationData: {
            type: Object as PropType<PaginationResponse>,
            required: true,
        },
        setPage: {
            type: Function,
            required: true,
        },
        setPerPage: {
            type: Function,
        },
        perPageOptions: {
            type: Array as PropType<number[]>,
            default: () => [10, 25, 50],
        },
    },

    setup(props) {
        return {
            currentPage: computed(() => props.paginationData.currentPage),
            firstPage: computed(() => props.paginationData.currentPage === 1),
            lastPage: computed(() => props.paginationData.currentPage === props.paginationData.totalPages),
            normalizedPerPageOptions: computed(() => [...new Set([...props.perPageOptions, props.paginationData.perPage])].sort((a, b) => a - b)),
            pageElements: computed(() => {
                const pages = [];
                const offset = 3;

                const currentPage = props.paginationData.currentPage;
                const totalPages = props.paginationData.totalPages;
                if (currentPage - offset > 1) {
                    pages.push(1);
                    pages.push('...');
                }

                for (let i = Math.max(1, currentPage - offset); i <= Math.min(totalPages, currentPage + offset); i++) {
                    pages.push(i);
                }

                if (currentPage + offset < totalPages) {
                    pages.push('...');
                    pages.push(totalPages);
                }

                return pages;
            }),
            onPerPageChange: (evt: Event) => {
                props.setPerPage?.(Number((evt.target as HTMLSelectElement).value));
            },
        };
    },
});
</script>
